/**
 * The per-code request block on a ticket card — Jay's workbook, reshaped around SKUs by Max on
 * 2026-08-30.
 *
 * A ticket covers a project GROUP, and 83 of the 143 groups hold more than one SKU (up to nineteen).
 * The first version of this form could hold ONE answer for the whole group — one colour, one size,
 * one deadline — which was wrong for most of them. So the block has two halves:
 *
 *   ticket-wide fields   asked once: incoterms, a deadline that covers the whole request
 *   the SKU list         tick the SKUs this ticket covers; each ticked SKU is one or more LINES,
 *                        and every field the form scopes to a SKU is a column — prefilled from that
 *                        SKU's own record, editable per line, because a request can legitimately
 *                        differ from what the SKU says.
 *
 * A ticked SKU can carry more than one line (Max, 2026-09-03: "1 SKU mà cần làm sample cho 2 màu
 * khác nhau"). Two colourways of the same item are two samples to make, two rows in the mail and two
 * things to chase, so they are two lines — not one row with "Black / Coyote" typed into a box.
 *
 * Which fields are which is DATA (`scope` on the form field), editable in the Request forms tab.
 * Jay's {{sku_table}} token still exists and now renders this list, so his block text is untouched.
 */
import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { BACKEND_URL } from './backendConfig';
import i18n from './i18n';
import { pickL10n } from './l10nField';

/* Declared widths, because the left of the table freezes and a sticky cell needs a real offset.
   Freezing runs through the FIRST field column — "tới cột category" (Max, 2026-09-03), which is what
   T1-01's first column is now that Item / SKU has gone; as a rule it is the column that says what
   kind of thing the row is, and every form puts that first. */
const TKF_W = { tick: 34, sku: 170, col: 150 };
const TKF_FROZEN_COLS = 1;
/* Wider than the default where the answer is longer than the heading (Max, 2026-09-04). Keyed by
   field, so a field that moves between forms keeps its width. */
const TKF_COL_W = {
  item_name: 220,
  dac_part_code: 200,
  frame_required_date: 190,
  sample_required_date: 190,
  costing_required_date: 190,
  spec_link: 220,
  bom_link: 220,
  special_requirement: 220,
  colour_material: 170,
  frame_colour: 190,
  assumed_qty: 180,
  vendor: 190,
  currency: 130,
  incoterms: 150,
  customer_entity: 200,
  customer_po_ref: 200,
  destination: 210,
  exf_date: 190,
  unit_price: 130,
  order_qty: 130,
  order_qty_basis: 160,
  frame_qty: 150,
  style_code: 170,
  hierarchy_code: 170,
  barcode_required: 150,
  cost_price: 130,
  selling_price: 140,
  target_launch: 160,
  po_number: 160,
  target_etd: 150,
  monitoring_point: 220,
  update_required_date: 170,
  sourcing_item: 210,
  spec_summary: 200,
  target_moq: 130,
  target_lead_time: 160,
  estimated_qty: 180,
  sourcing_required_date: 200,
};
const tkfColW = (col) => TKF_COL_W[col.key] || TKF_W.col;
/* Column 0 is the tick, 1 is the SKU, 2.. are the form's own. */
const tkfLeft = (cols, i) => {
  if (i === 0) return 0;
  if (i === 1) return TKF_W.tick;
  return TKF_W.tick + TKF_W.sku
    + cols.slice(0, i - 2).reduce((sum, c) => sum + tkfColW(c), 0);
};

/* Cleared when a line is copied to make another one — see addLine. Keys, not labels: the form is
   data, and these are the two a second line exists to change. */
const COLOUR_KEYS = ['colour_material', 'colour', 'frame_colour'];

/* Where a SKU column's prefill comes from on the project SKU record. Anything not here starts blank. */
const PREFILL = {
  /* subItem is the SKU's OWN name (projects.item_name); projectItem is the project name, which is
     the same string for every SKU in the group — using it printed "Blue Banana Quilt" against both
     SKU-359 and SKU-360 when they are Chair One and Chair One (re). */
  item_name: (s) => s.subItem || s.projectItem || '',
  colour_material: (s) => s.colourMaterial || '',
  size: (s) => s.sizeText || '',
  style_code: (s) => s.styleCode || '',
  hierarchy_code: (s) => s.hierarchyCode || '',
  frame_type: (s) => s.frameType || '',
  frame_colour: (s) => s.frameColor || '',
  dac_part_code: (s) => s.dacPartCode || '',
  vendor: (s) => s.vendor || '',
  barcode_required: (s) => s.barcodeRequired || '',
  estimated_qty: (s) => (s.estimateQty ?? '') === null ? '' : String(s.estimateQty ?? ''),
  order_qty: (s) => (s.estimateQty ?? '') === null ? '' : String(s.estimateQty ?? ''),
  target_price: (s) => String(s.targetPrice ?? ''),
  exf_date: (s) => s.exfShipDate || '',
  product_category: (s) => s.category || '',
  season: (s) => s.season || '',
};

/* The answers for one SKU: a list of lines. An older ticket stored a single object for a SKU, and a
   freshly ticked one has nothing yet — both read as one empty line, so nothing else has to care. */
export const linesOf = (bag, sku) => {
  const v = ((bag || {}).bySku || {})[sku];
  if (Array.isArray(v)) return v.length ? v : [{}];
  return [v || {}];
};

export const skuColumns = (form) => (form
  ? form.fields.filter((f) => f.scope === 'sku' && f.type !== 'table' && f.type !== 'materials') : []);

/* A form that asks WHICH MATERIALS of a SKU are being requested — vendor sourcing, so far (Max,
   2026-09-04: "khi tick SKU thì sẽ hiện thêm dòng material list của SKU đó và user sẽ tick xem cái
   material nào cần source ở Vendor"). Declared as a field of type `materials`, the way `sku_table`
   declares the SKU list: it is a marker, not a column. */
export const wantsMaterials = (form) => !!form && form.fields.some((f) => f.type === 'materials');

/* The line a material owns, if it has been picked. `_mat` is the material's position in the SKU's
   own list — the list has no id of its own, and its rows are rewritten wholesale on every save, so
   the position is what there is. */
export const MATERIAL_KEY = '_mat';
export const ticketFields = (form) => (form
  ? form.fields.filter((f) => f.scope !== 'sku') : []);

/** Every required answer still missing — the ticket-wide ones, and any on a ticked SKU's row. */
/* A quantity that nobody knows yet. Max, 2026-09-09: the box takes a number, "nhưng đôi khi lại
   chưa xác định được con số nên sẽ điền là TBC".

   <input type="number"> cannot hold that at all — the browser refuses the keystrokes and hands back
   an empty string, so the answer silently becomes "no answer". `qty` is therefore a text box that
   takes a number OR the word.

   Its own field type, not a loosening of `number`: prices, weights, CBM and cartons are number
   fields too, and "TBC" in a unit price is a different and much worse thing than in a quantity.

   What people type is normalised to ONE spelling — tbc, TBD, tba, "T.B.C" all land on TBC — because
   a column holding four spellings of the same answer can be neither read down nor counted. */
export const QTY_UNKNOWN = 'TBC';
const QTY_UNKNOWN_RE = /^t\.?\s*b\.?\s*[cad]\.?$/i;
export const normaliseQty = (v) => {
  const text = String(v ?? '').trim();
  return QTY_UNKNOWN_RE.test(text) ? QTY_UNKNOWN : text;
};

/* AN ANSWER THAT DOES NOT COUNT. Blank, or — in a NUMBER field — not a number. Max, 2026-10-16:
   Estimated order quantity, Quantity (sets) and Quantity basis "chỉ được điền số không được điền TBC",
   so those three went from `qty` (number or TBC) to `number`. The box itself refuses letters, but an
   answer saved while the field was still a `qty` box can hold the word, and a box that cannot show
   "TBC" would read as filled to a check that only asked whether it was empty. The server reads the
   same rule (ticketMissingRequired). */
export const answerMissing = (f, v) => {
  const text = String(v ?? '').trim();
  if (text === '') return true;
  return f.type === 'number' && !Number.isFinite(Number(text.replace(/,/g, '')));
};

export const missingRequired = (form, values) => {
  if (!form) return [];
  const out = [];
  const bag = values || {};
  ticketFields(form).forEach((f) => {
    if (f.required && answerMissing(f, bag[f.key])) out.push(pickL10n(f, 'label'));
  });
  const picked = Array.isArray(bag.skus) ? bag.skus : [];
  const cols = skuColumns(form);
  /* Read in the reader's language: the list is shown on the ticket card ("Still needed …"), and
     only its LENGTH is ever tested. The field labels are the form's own (data). */
  if (cols.length && !picked.length) out.push(i18n.t('xTicket.missingOneSku'));
  picked.forEach((sku) => {
    linesOf(bag, sku).forEach((row, i) => {
      const where = i ? i18n.t('xTicket.missingSkuLine', { sku, n: i + 1 }) : sku;
      cols.forEach((f) => {
        if (f.required && answerMissing(f, row[f.key])) out.push(`${pickL10n(f, 'label')} (${where})`);
      });
    });
  });
  return out;
};

/** The request block with its tokens filled. Blank answers print a dash, never "{{token}}".
 *
 * Jay wrote every block as one line per question, which was right when a ticket meant one SKU. Now
 * that it covers several, a line whose tokens are ALL per-SKU cannot be one line any more — the run
 * of them is replaced, in place, by the SKU list. The ticket-wide lines keep his wording and their
 * order. A form that already had {{sku_table}} keeps using it; this is the same list either way. */
/* KOREAN BESIDE THE ENGLISH — Jay, 2026-10-01: "Put Korean in the email as well as English. Same
   content, Korean below the English block. The recipients are HINC Korea."

   THE LABELS ARE SWAPPED, NOT THE TEMPLATE RE-WRITTEN. body_block carries its labels inside the
   text — "- Category: {{product_category}}" — so a second Korean template would be a second thing
   to keep in step with the first, and the day they disagree the mail says two different things.
   Each line is matched to its own token instead, and the words before the colon are replaced with
   that field's label_ko. A field with no Korean keeps its English, so a field added tomorrow is
   readable in both halves from the moment it exists.

   VALUES ARE NEVER TOUCHED. Only the frame is translated: what the requester typed stays exactly
   as they typed it (Max, 2026-10-04). Machine-translating a technical instruction is how the
   receiving team is told to do something nobody asked for, and nobody would be able to see it. */
const koLabelFor = (form, token) => {
  const f = (form.fields || []).find((x) => x.key === token);
  return (f && f.labelKo) || '';
};
/* "- English label: value" -> "- 한국어 라벨: value". Anchored on the FIRST colon, because a label
   can legitimately contain a slash or brackets but never a colon. */
const koSwapLabel = (line, form, tokens) => {
  const m = line.match(/^(\s*-\s*)([^:]+)(:\s*)(.*)$/);
  if (!m) return line;
  const ko = tokens.map((t) => koLabelFor(form, t)).find(Boolean);
  return ko ? `${m[1]}${ko}${m[3]}${m[4]}` : line;
};

export const renderBlock = (form, values, skuNames = {}, lang = 'en') => {
  if (!form) return '';
  const ko = lang === 'ko';
  const bag = values || {};
  const picked = Array.isArray(bag.skus) ? bag.skus : [];
  const cols = skuColumns(form);
  const skuKeys = new Set(form.fields.filter((f) => f.scope === 'sku').map((f) => f.key));

  const list = () => {
    if (!cols.length) return '';
    if (!picked.length) return ko ? '- 품목: -' : '- Items: -';
    /* Counted in LINES, not SKUs: two colourways of one SKU are two samples to make. */
    const all = picked.flatMap((sku) => linesOf(bag, sku).map((row) => [sku, row]));
    return `${ko ? '- 품목' : '- Items'} (${all.length}):\n${all.map(([sku, row]) => {
      const parts = cols.map((c) => `${(ko && c.labelKo) || c.label}: ${String(row[c.key] ?? '').trim() || '-'}`);
      return `    - ${`${sku} ${skuNames[sku] || ''}`.trim()}`
        + (parts.length ? `\n        ${parts.join(' / ')}` : '');
    }).join('\n')}`;
  };

  const fill = (line) => line.replace(/\{\{(\w+)\}\}/g,
    (whole, key) => String(bag[key] ?? '').trim() || '-');

  const out = [];
  let listWritten = false;
  form.body.split('\n').forEach((line) => {
    const tokens = (line.match(/\{\{(\w+)\}\}/g) || []).map((t) => t.slice(2, -2));
    const perSku = tokens.length > 0 && tokens.every((t) => skuKeys.has(t));
    if (!perSku) {
      /* The heading line carries no token, so it is matched on the title itself. */
      if (ko && form.titleKo && line.trim() === String(form.title || '').trim()) { out.push(form.titleKo); return; }
      out.push(ko ? koSwapLabel(fill(line), form, tokens) : fill(line));
      return;
    }
    if (listWritten) return;                 // the rest of the run is already in the list
    const block = list();
    if (block) out.push(block);
    listWritten = true;
  });
  return out.join('\n');
};

/* The same block as HTML, with the SKU list as a real table.
 *
 * NOT a second template: this walks the same body, the same fields and the same answers as
 * renderBlock — one function, two outputs. The mail has always been sent as text AND html
 * (taskTrackerSendEmail), and the html half was only the text with <br> between the lines, so a
 * seven-column list arrived as a wall of "a / b / c". */
const esc = (v) => String(v ?? '').replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;');
/* An ANSWER may contain line breaks; the template around it may not. A newline is whitespace in
   html, so a three-line address typed into a textarea arrived as one run-on line and two signatories
   read as one person (Max, 2026-09-05). Only values go through this — escaping the template's own
   newlines would double the line breaks the assembler below already adds. */
const escLines = (v) => esc(v).replace(/\r?\n/g, '<br>');
/* Every cell is fixed to its column's width and wraps inside it (Max, 2026-09-04). Without that one
   long answer — a pasted vendor address, a link — stretched its column across the whole mail and
   pushed everything after it out of view. `word-break` as well as `overflow-wrap`, because a value
   with no spaces in it (a URL, or somebody leaning on a key) has nowhere to wrap otherwise.

   The widths are the same TKF_COL_W the screen uses, so the mail and the form read alike. */
/* box-sizing matters more than it looks. `width` on a cell is content-box by default, so every
   declared width was quietly 20px short of what it rendered — 18px of padding and 2px of border per
   column, which on eleven columns is 210px the table was wider than the number said. Measured:
   declared 1047, rendered 1257. With border-box the number means what it says, and the widths below
   are the whole cell.

   The wrapping is deliberate and always on (Max, 2026-09-07: "nhớ là các nội dung trong bảng luôn
   được wrap text nha"): break-word so a long value folds instead of stretching its column, and
   overflow-wrap:anywhere so a URL with no spaces in it folds too. Nothing here is nowrap. */
const TD_BASE = 'box-sizing:border-box;padding:5px 9px;border:1px solid #d7dde5;font-size:13px;'
  + 'vertical-align:top;word-break:break-word;overflow-wrap:anywhere;';
const cellStyle = (w) => `${TD_BASE}width:${w}px;max-width:${w}px;`;
const headStyle = (w) => `${cellStyle(w)}background:#123B6D;color:#fff;font-weight:700;text-align:left;`;
/* Measured in the mail's own font (Arial 13px) rather than guessed, after three rounds of guessing
   — Max, 2026-09-07: "giảm width SKU, Item, Special requirement".

   sku 76   "SKU-481" in bold is 54px, and 76 is that plus the 20px the cell spends on padding and
            border. It does not go lower: the cells break anywhere, so a narrower column splits the
            code into "SKU-" / "481", and a reader scanning a column of ids is who that hurts.
   item 120 "Light shell mini cross bag(tbc)" is 172px on one line and its longest word 77, so 120
            folds it onto two lines and still never breaks a word. */
const MAIL_W = { sku: 76, item: 120 };

/* The mail needs its own widths — Max, 2026-09-07: "cần điều chỉnh lại width các cột trên email nhé".

   The screen's widths size an INPUT: a Quantity box is 150px because that is a comfortable number
   field to click into. The mail prints a value, and the same column carries "1". Nine columns each
   holding a word in a 150px cell made the table 1,810px wide, which pushed Special requirement — the
   one column with a sentence in it — into wrapping onto two lines while Size sat on 150px of nothing.

   Driven by the field's TYPE, so it works for all seventeen forms rather than the one that was
   looked at: a number is a number in every form. The per-key list is only for fields whose type
   understates them — `special_requirement` is a plain text field carrying a sentence. */
/* Second pass, 2026-09-07: Max marked SKU, Season, Sample type, Quantity, Color and Required date
   as still too wide once he saw them in an inbox. A select holding "OEM/ODM" or "27SS" needs 100,
   not 120; a date is ten characters; a quantity is usually one. Their headings wrap to two lines at
   these widths, which is the trade being made deliberately — the values are what the reader scans,
   and a heading is read once. */
const MAIL_TYPE_W = { number: 60, date: 90, select: 90, url: 140, textarea: 180, checkbox: 70 };
const MAIL_COL_W = {
  /* Narrowed on 2026-09-08, form by form, from reading them in an inbox. Every one of these is still
     floored by its own heading, so none of them can be cut to the point of breaking a word. */
  dac_part_code: 120,
  order_qty_basis: 110,
  style_code: 110,
  hierarchy_code: 120,
  barcode_required: 80,
  currency: 70,
  /* A type default cannot know that Season holds "27SS" while another select holds a sentence, so
     the columns whose OPTION LISTS are short are named here (Max, 2026-09-07, third pass). */
  season: 62,
  sample_type: 72,
  sample_qty: 73,
  product_category: 90,
  size: 55,
  colour_material: 92,
  frame_colour: 110,
  /* A sentence, so it wraps by design. "Development requested from JE to DEV on 1-Sep" is 288px on
     one line and its longest word is 76; 170 gives it two or three lines and no broken word. */
  special_requirement: 170,
  monitoring_point: 170,
  sourcing_item: 150,
  spec_summary: 150,
  item_name: 150,
};
/* ...but never narrower than the heading's LONGEST WORD, MEASURED.

   Character counts do not work here and three rounds of this proved it: in the mail's bold 13px
   Arial, "SKU" costs 9.3px a character and "Quantity" costs 6.6, so any single multiplier is too
   small for one of them. At 6.6 the Season column came out 62px and Chrome broke the heading into
   "Seaso" over "n"; at 7.2 it came out 64 and still did. Canvas measures the actual string in the
   actual font, which is the only answer that does not need a fourth round.

   The estimate stays as a fallback for a context with no canvas — deliberately generous, at the
   widest per-character cost observed, because under-sizing is what breaks a word. */
let mailCtx;
const mailTextW = (text, bold) => {
  if (mailCtx === undefined) {
    try { mailCtx = document.createElement('canvas').getContext('2d') || null; } catch (e) { mailCtx = null; }
  }
  const s = String(text || '');
  if (!mailCtx) return Math.ceil(s.length * 9.4);
  mailCtx.font = `${bold ? '700 ' : ''}13px Arial, Helvetica, sans-serif`;
  return Math.ceil(mailCtx.measureText(s).width);
};

/* Per-form decisions about the MAIL's columns, made by Max form by form on 2026-09-08 after reading
   a sample of each in his inbox. None of it changes what the form ASKS — the requester still answers
   every field on screen; this is only about what the table prints.

   Three of the forms carry an item name of their own AND the Item column the table adds from the
   project, which is the same product named twice. Which of the pair goes differs by form, and that
   is deliberate rather than untidy: in PLM & NetSuite registration "Item name" is the name being
   registered in NetSuite and is meant to differ from the project's, so the project's goes; in the two
   frame forms "Item / SKU" is just restating the SKU, so it goes and the project's stays. */
const MAIL_DROP_ITEM_COL = new Set(['plm_netsuite_item_registration']);
const MAIL_DROP_FIELDS = {
    bulk_frame_booking_dac: new Set(['item_name']),
    sample_frame_procurement: new Set(['item_name']),
};

/* The Item column is what a reader scans down, and on a short table there is room to let it breathe:
   "Light shell mini cross bag(tbc)" needs 192px and was folding onto three lines inside 120. Keyed on
   how many columns the form asks for rather than on a list of form names, so a form added later gets
   the same treatment without anybody remembering to add it. Max named exactly the four forms this
   rule reaches. */
const MAIL_ITEM_W_WIDE = 200;
const MAIL_SHORT_FORM_COLS = 3;

const mailLabelFloor = (label) => Math.min(190, String(label || '').split(/[\s/]+/)
  .reduce((widest, word) => Math.max(widest, mailTextW(word, true)), 0) + 20);

const mailColW = (col) => Math.max(
  MAIL_COL_W[col.key] || MAIL_TYPE_W[col.type] || Math.min(tkfColW(col), 170),
  mailLabelFloor(col.label),
);

export const renderBlockHtml = (form, values, skuNames = {}, lang = 'en') => {
  if (!form) return '';
  const ko = lang === 'ko';
  const bag = values || {};
  const picked = Array.isArray(bag.skus) ? bag.skus : [];
  /* The columns the MAIL prints. Not the same list as the screen's: a form can ask for something the
     table has no room to repeat, and dropping it here leaves the question on the form where the
     requester still answers it. */
  const dropped = MAIL_DROP_FIELDS[form.formKey] || new Set();
  const cols = skuColumns(form).filter((c) => !dropped.has(c.key));
  const withItem = !MAIL_DROP_ITEM_COL.has(form.formKey);
  // Room to breathe on a short table; see MAIL_ITEM_W_WIDE.
  const itemW = cols.length <= MAIL_SHORT_FORM_COLS ? MAIL_ITEM_W_WIDE : MAIL_W.item;
  const skuKeys = new Set(form.fields.filter((f) => f.scope === 'sku').map((f) => f.key));

  const table = () => {
    if (!cols.length) return '';
    if (!picked.length) {
      return `<p style="margin:6px 0;color:#8a94a6;">${ko ? '선택된 SKU 없음.' : 'No SKU picked.'}</p>`;
    }
    const head = `<tr><th style="${headStyle(MAIL_W.sku)}">SKU</th>`
      + (withItem ? `<th style="${headStyle(itemW)}">Item</th>` : '')
      + `${cols.map((c) => `<th style="${headStyle(mailColW(c))}">${esc((ko && c.labelKo) || c.label)}</th>`).join('')}</tr>`;
    const rows = picked.flatMap((sku) => linesOf(bag, sku).map((row) => (
      `<tr><td style="${cellStyle(MAIL_W.sku)}font-weight:700;">${esc(sku)}</td>`
        + (withItem ? `<td style="${cellStyle(itemW)}">${esc(skuNames[sku] || '')}</td>` : '')
        + cols.map((c) => `<td style="${cellStyle(mailColW(c))}">`
          + `${escLines(String(row[c.key] ?? '').trim() || '-')}</td>`).join('')
        + '</tr>'
    ))).join('');
    /* table-layout: fixed, or a long value overrides the declared widths and the whole point is
       lost — the same rule the two tables on screen already follow. */
    const total = MAIL_W.sku + (withItem ? itemW : 0) + cols.reduce((sum, c) => sum + mailColW(c), 0);
    return `<table cellspacing="0" cellpadding="0" style="border-collapse:collapse;margin:8px 0 10px;`
      + `table-layout:fixed;width:${total}px;">${head}${rows}</table>`;
  };

  const fill = (line) => esc(line).replace(/\{\{(\w+)\}\}/g,
    (whole, key) => escLines(String(bag[key] ?? '').trim() || '-'));

  const out = [];
  let written = false;
  form.body.split('\n').forEach((line) => {
    const tokens = (line.match(/\{\{(\w+)\}\}/g) || []).map((t) => t.slice(2, -2));
    const perSku = tokens.length > 0 && tokens.every((t) => skuKeys.has(t));
    if (!perSku) {
      /* Swapped BEFORE filling, so a value that happens to contain a colon cannot be mistaken for
         the label's own. The heading carries no token and is matched on the title. */
      if (ko && form.titleKo && line.trim() === String(form.title || '').trim()) { out.push(esc(form.titleKo)); return; }
      out.push(fill(ko ? koSwapLabel(line, form, tokens) : line));
      return;
    }
    if (written) return;
    const t = table();
    if (t) out.push(`\u0000TABLE\u0000${t}`);
    written = true;
  });
  /* A line that is the table stands alone; everything else keeps the plain-text line breaks. */
  return out.map((l) => (l.startsWith('\u0000TABLE\u0000') ? l.slice(7) : `${l}<br>`)).join('');
};

export default function TicketFormFields({ form, values, onChange, skus = [], disabled, takenSkus = {} }) {
  const { t, i18n: tr } = useTranslation();
  const lang = tr.language;   // the form's title and field labels in the reader's language (pickL10n)
  /* AN ITEM ALREADY ASKED FOR UNDER THIS CODE CANNOT BE ASKED FOR AGAIN — Max, 2026-09-21: "cần
     biết là SKU nào đã dc chọn rồi thì ko cho tick nữa". The same code can now be raised more than
     once on a project, which is the point (three items go out, two more are added a month later,
     the second ticket carries the same request for those two) — and the thing that makes that safe
     is that the second ticket cannot quietly repeat the first. `takenSkus` maps the SKU to the
     ticket that already holds it; the caller leaves THIS ticket out of it, or editing one would
     find every row it owns locked against itself. */
  const takenBy = (id) => takenSkus[id] || '';
  /* The material list of every SKU this ticket has ticked. Fetched here rather than passed in: the
     ticket card knows the project's SKUs, not what each is made of, and only a form that asks for
     materials needs them at all. */
  const [materials, setMaterials] = useState({});
  const byMaterial = wantsMaterials(form);
  const pickedSkus = (values && Array.isArray(values.skus)) ? values.skus : [];
  const needed = byMaterial ? pickedSkus.join(',') : '';
  useEffect(() => {
    if (!needed) return undefined;
    let alive = true;
    const wanted = needed.split(',').filter(Boolean);
    Promise.all(wanted.map(async (sku) => {
      const r = await fetch(
        `${BACKEND_URL}/api/development/project-tracker/sku-materials/${encodeURIComponent(sku)}`,
        { credentials: 'include' },
      );
      const d = await r.json().catch(() => ({}));
      return [sku, r.ok ? (d.materials || []) : []];
    })).then((pairs) => { if (alive) setMaterials((prev) => ({ ...prev, ...Object.fromEntries(pairs) })); })
      .catch(() => {});
    return () => { alive = false; };
  }, [needed]);

  if (!form) return null;
  const bag = values || {};
  const picked = Array.isArray(bag.skus) ? bag.skus : [];
  const cols = skuColumns(form);
  const wide = ticketFields(form);

  const setTicket = (key) => (e) => onChange({ ...bag, [key]: e.target.value });

  /* FILL THE COLUMN FROM THE FIRST TICKED SKU — Max, 2026-09-22, relaying the people who use it:
     "họ chỉ cần điền 1 ô đầu và sau đó click thì các ô cùng cột sẽ nhận giá trị". PRJ-00183 asks for
     Sample type, Colour and Required date on thirteen SKUs; that is thirty-nine identical values
     typed by hand, and the warning under the table lists all thirty-nine until they are.

     ONLY TICKED SKUs. An unticked one is not part of this ticket — it renders a dash, not an input —
     and writing into it would fill a row nobody asked for and that nothing will send.

     EVERY LINE of each ticked SKU, not just the first. A SKU can carry several lines (one per
     colourway, or one per material on a sourcing form), and "the same column" means all of them.

     IT OVERWRITES, which is what fill-down means everywhere else, and it is one deliberate click per
     column. What it will not do is overwrite with NOTHING: with the source cell empty the button is
     dead, because a stray click emptying a column somebody had filled is the one way this could
     cost more than it saves. */
  const firstTicked = skus.find((x) => picked.includes(x.id));
  const fillValue = (key) => {
    if (!firstTicked) return '';
    return String(linesOf(bag, firstTicked.id)[0]?.[key] ?? '');
  };
  const canFill = (key) => !disabled && picked.length > 1 && fillValue(key) !== '';
  const fillDown = (key) => () => {
    const v = fillValue(key);
    if (v === '') return;
    const next = { ...(bag.bySku || {}) };
    picked.forEach((id) => {
      next[id] = linesOf(bag, id).map((row) => ({ ...row, [key]: v }));
    });
    onChange({ ...bag, bySku: next });
  };

  const putLines = (sku, lines) => onChange({
    ...bag, bySku: { ...(bag.bySku || {}), [sku]: lines },
  });
  const setCell = (sku, i, key) => (e) => putLines(sku,
    linesOf(bag, sku).map((row, x) => (x === i ? { ...row, [key]: e.target.value } : row)));

  /* A second line starts as a copy of the one above it: the case this exists for is the same item in
     another colourway, so everything but the colour is already right. The colour is cleared so the
     new line cannot be sent as a silent duplicate of the first. */
  const addLine = (sku) => () => {
    const lines = linesOf(bag, sku);
    const copy = { ...lines[lines.length - 1] };
    COLOUR_KEYS.forEach((k) => { if (k in copy) copy[k] = ''; });
    putLines(sku, [...lines, copy]);
  };
  const dropLine = (sku, i) => () => putLines(sku, linesOf(bag, sku).filter((_, x) => x !== i));

  /* ---- materials ---------------------------------------------------------------------------- */
  const matsOf = (sku) => materials[sku] || [];
  const lineOfMaterial = (sku, seq) => linesOf(bag, sku)
    .findIndex((l) => String(l[MATERIAL_KEY] ?? '') === String(seq));
  /* Ticking a material adds its line; unticking removes it. The line carries what the material
     already says, so the request does not ask somebody to retype the name of a thing the SKU
     already records. */
  const toggleMaterial = (sku, m, seq) => () => {
    const lines = linesOf(bag, sku).filter((l) => l[MATERIAL_KEY] !== undefined || matsOf(sku).length === 0);
    const at = lines.findIndex((l) => String(l[MATERIAL_KEY] ?? '') === String(seq));
    if (at >= 0) { putLines(sku, lines.filter((_, x) => x !== at)); return; }
    const seed = { [MATERIAL_KEY]: String(seq) };
    if (cols.some((c) => c.key === 'sourcing_item')) seed.sourcing_item = m.name || '';
    if (cols.some((c) => c.key === 'spec_summary')) seed.spec_summary = m.spec || '';
    if (cols.some((c) => c.key === 'colour_material')) seed.colour_material = m.colour || '';
    putLines(sku, [...lines, seed]);
  };

  /* Ticking a SKU fills its row from the SKU's own record — the whole point of putting those fields
     on the SKU. Unticking keeps the answers, so a mis-click does not throw away typing. */
  const toggle = (sku) => () => {
    if (picked.includes(sku)) {
      onChange({ ...bag, skus: picked.filter((s) => s !== sku) });
      return;
    }
    const row = skus.find((s) => s.id === sku) || {};
    const prefill = {};
    cols.forEach((c) => {
      const from = PREFILL[c.key];
      const v = from ? from(row) : '';
      if (String(v ?? '').trim() !== '') prefill[c.key] = String(v);
    });
    const kept = linesOf(bag, sku);
    onChange({
      ...bag,
      skus: [...picked, sku],
      // Unticking keeps the lines, so re-ticking must not overwrite them with the prefill again.
      bySku: { ...(bag.bySku || {}), [sku]: kept.map((line, i) => (i ? line : { ...prefill, ...line })) },
    });
  };

  /* Tick every SKU at once — Max, 2026-09-07: "thêm chỗ tick all". A ticket that covers the whole
     project is the ordinary case, and ticking twenty boxes by hand was the tax on it. It runs the
     same prefill each box would have run, so the rows arrive filled exactly as hand-ticking fills
     them; clearing keeps the lines, exactly as unticking one does. */
  // "All" means all the ones this ticket may have: a row that belongs to another ticket of the
  // same code is not part of "all", or the header box would tick what every row box refuses to.
  const free = skus.filter((s) => !takenBy(s.id));
  const allPicked = free.length > 0 && free.every((s) => picked.includes(s.id));
  const toggleAll = () => {
    if (allPicked) { onChange({ ...bag, skus: [] }); return; }
    const bySku = { ...(bag.bySku || {}) };
    free.forEach((s) => {
      if (picked.includes(s.id)) return;
      const prefill = {};
      cols.forEach((c) => {
        const from = PREFILL[c.key];
        const v = from ? from(s) : '';
        if (String(v ?? '').trim() !== '') prefill[c.key] = String(v);
      });
      bySku[s.id] = linesOf(bag, s.id).map((line, i) => (i ? line : { ...prefill, ...line }));
    });
    onChange({ ...bag, skus: free.map((s) => s.id), bySku });
  };

  /* How far in the frozen block reaches: the tick, the SKU, and the first columns of the form. */
  const lastFrozen = 1 + Math.min(TKF_FROZEN_COLS, cols.length);
  const fz = (i) => (i <= lastFrozen
    ? { className: `tkf-fz${i === lastFrozen ? ' tkf-fz-last' : ''}`, style: { left: tkfLeft(cols, i) } }
    : {});

  const cell = (f, value, onCellChange) => {
    const common = { value: value ?? '', onChange: onCellChange, disabled };
    if (f.type === 'select' && f.options.length) {
      /* A value the list does not hold still shows. The SKU's own frame colour is the case this
         exists for (Max, 2026-09-04: "cột colour có thể dùng thông tin trong cột Frame colour"):
         a SKU says "F08 Navy" or "Olive Green", while the PLM list holds the composite
         "F:F08Navy / H:F15Silver / S:F15Silver" — a plain select would have rendered that as blank
         and quietly lost what the SKU actually said. */
      const v = String(value ?? '');
      const off = v && !f.options.includes(v);
      return (
        <select {...common}>
          <option value="">—</option>
          {off && <option value={v}>{v}</option>}
          {f.options.map((o) => <option key={o} value={o}>{o}</option>)}
        </select>
      );
    }
    /* A multiselect HAS to stay a text box — several answers go in one cell — but when its list is
       known the box should offer it. Documents required has held CIPL / BL / COO / Packing list all
       along and still said "not yet a fixed list", which is the opposite of true. */
    if (f.type === 'multiselect' && f.options.length) {
      const listId = `tkf-opt-${f.key}`;
      return (
        <>
          <input {...common} list={listId} placeholder={t('xTicket.pickOrType')} />
          <datalist id={listId}>
            {f.options.map((o) => <option key={o} value={o} />)}
          </datalist>
        </>
      );
    }
    if (f.type === 'select' || f.type === 'multiselect') {
      return <input {...common} placeholder={t('xTicket.notFixedList')} />;
    }
    if (f.type === 'boolean') {
      return (
        <select {...common}>
          {/* The VALUE stays the English word — it is what the mail prints and the server keeps. */}
          <option value="">—</option>
          <option value="Yes">{t('xTicket.yes')}</option>
          <option value="No">{t('xTicket.no')}</option>
        </select>
      );
    }
    /* A field somebody writes a sentence into. A registered address or the scope of an NDA does not
       fit a one-line box, and typing into one that scrolls sideways is how a detail gets left out
       (Max, 2026-09-04). Two rows here, taller in the ticket-wide grid where it has the room. */
    if (f.type === 'textarea') return <textarea rows={2} {...common} />;
    if (f.type === 'number') return <input type="number" step="any" {...common} />;
    /* Normalised on blur rather than on every keystroke: rewriting the box while someone is still
       typing "tbc" takes the cursor away from them. Both change handlers read e.target.value, so the
       shape passed here is the shape they expect. */
    if (f.type === 'qty') {
      return (
        <input
          {...common}
          inputMode="numeric"
          placeholder={t('xTicket.numberOrUnknown', { word: QTY_UNKNOWN })}
          onBlur={(e) => {
            const next = normaliseQty(e.target.value);
            if (next !== e.target.value) onCellChange({ target: { value: next } });
          }}
        />
      );
    }
    if (f.type === 'date') return <input type="date" {...common} />;
    if (f.type === 'url' || f.type === 'file') return <input type="url" placeholder="https://…" {...common} />;
    if (f.type === 'email') return <input type="email" placeholder="name@vendor.com" {...common} />;
    return <input {...common} />;
  };

  return (
    <div className="tkf">
      {/* The form's own words in the reader's language (kept in the database, sql/20261021i); the MAIL below is
          built from the English and the Korean, whatever the reader's language. */}
      <div className="tkf-head">{pickL10n(form, 'title', lang)}</div>

      {!!wide.length && (
        <div className="tkf-grid">
          {wide.map((f) => {
            const empty = f.required && answerMissing(f, bag[f.key]);
            // A long field takes the whole row rather than one column of the grid.
            return (
              <label
                className={`tkf-field ${empty ? 'missing' : ''}${f.type === 'textarea' ? ' tkf-long' : ''}`}
                key={f.key}
              >
                <span>{pickL10n(f, 'label', lang)}{f.required && <em> *</em>}</span>
                {cell(f, bag[f.key], setTicket(f.key))}
              </label>
            );
          })}
        </div>
      )}

      {!!cols.length && (
        <div className="tkf-skus">
          <div className="tkf-table-head">
            {t('xTicket.skusCovered')}
            {/* "0 of 5" over a list where three rows cannot be ticked is the counter describing a
                table somebody else's ticket owns half of. It counts what this ticket may have, and
                says separately how much is already spoken for. */}
            <span className="tkf-count">
              {t('xTicket.tickedCount', { n: picked.length, total: free.length })}
              {skus.length > free.length && ` · ${t('xTicket.alreadyAskedFor', { n: skus.length - free.length })}`}
            </span>
          </div>
          <div className="tkf-skuscroll">
            <table
              className="sp-table tkf-skutable"
              style={{
                minWidth: TKF_W.tick + TKF_W.sku + cols.reduce((sum, c) => sum + tkfColW(c), 0),
              }}
            >
              <colgroup>
                <col style={{ width: TKF_W.tick }} />
                <col style={{ width: TKF_W.sku }} />
                {cols.map((c) => <col key={c.key} style={{ width: tkfColW(c) }} />)}
              </colgroup>
              <thead>
                <tr>
                  <th {...fz(0)} className={`tkf-tick ${fz(0).className || ''}`}>
                    <input
                      type="checkbox"
                      checked={allPicked}
                      disabled={disabled || !free.length}
                      onChange={toggleAll}
                      title={t('xTicket.tickAll')}
                      aria-label={t('xTicket.tickAll')}
                      /* Half-ticked while some are on, so the box says which of the three states it
                         is in rather than reading as "none" whenever it is not "all". */
                      ref={(el) => { if (el) el.indeterminate = !allPicked && picked.length > 0; }}
                    />
                  </th>
                  <th {...fz(1)}>SKU</th>
                  {cols.map((c, ci) => (
                    <th key={c.key} {...fz(ci + 2)}>
                      {pickL10n(c, 'label', lang)}{c.required && <em> *</em>}
                      {/* On the header, because the thing being filled is the COLUMN. Beside the
                          first cell it would read as belonging to that SKU's row. */}
                      <button
                        type="button"
                        className="tkf-filldown"
                        disabled={!canFill(c.key)}
                        onClick={fillDown(c.key)}
                        title={canFill(c.key)
                          ? t('xTicket.fillDownTitle', {
                            value: fillValue(c.key), sku: firstTicked ? firstTicked.id : '', n: picked.length,
                          })
                          : (picked.length < 2
                            ? t('xTicket.fillNeedTwo')
                            : t('xTicket.fillNeedFirst'))}
                        aria-label={t('xTicket.fillDownAria', { label: pickL10n(c, 'label', lang) })}
                      >
                        &#8595;
                      </button>
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {/* One SKU is one or more lines. The tick and the name sit on the first line only,
                    so the extra lines read as belonging to it rather than as another SKU. */}
                {skus.flatMap((s) => {
                  const on = picked.includes(s.id);
                  const mats = byMaterial ? matsOf(s.id) : [];
                  /* A form that asks for materials turns the ticked SKU into one row per material,
                     each with its own tick: the vendor sources a material, not a SKU. A SKU whose
                     material list is empty falls back to the plain line behaviour, or the ticket
                     could not be raised at all until somebody fills that list in. */
                  if (on && byMaterial && mats.length) {
                    return [
                      /* The heading row carries NO colSpan. A cell cannot be both spanned and
                         frozen: the frozen block pins it to the SKU column's width while the span
                         lays its text out across the whole table, so the text escaped the column and
                         painted over the scrolling cells beside it — the frozen lane sits in a higher
                         layer, so it bled on top rather than under (Max, 2026-09-05: "bị lỗi UI khi
                         dòng SKU bị chạy nội dung"). Every frozen lane gets its own cell, and only
                         the columns past the freeze are spanned. */
                      <tr key={`${s.id}#head`} className="on tkf-mathead">
                        <td {...fz(0)} className={`tkf-tick ${fz(0).className || ''}`}>
                          <input type="checkbox" checked disabled={disabled} onChange={toggle(s.id)} />
                        </td>
                        <td {...fz(1)} className={`tkf-skuname ${fz(1).className || ''}`}>
                          <strong>{s.id}</strong>
                          <small>{s.subItem || s.projectItem || ''}</small>
                        </td>
                        <td {...fz(2)} className={`tkf-matnote ${fz(2).className || ''}`}>
                          {t('xTicket.materialsNote', { n: mats.length })}
                        </td>
                        {cols.length > 1 && <td colSpan={cols.length - 1} />}
                      </tr>,
                      ...mats.map((m, mi) => {
                        const seq = mi + 1;
                        const at = lineOfMaterial(s.id, seq);
                        const row = at >= 0 ? linesOf(bag, s.id)[at] : {};
                        return (
                          <tr key={`${s.id}#m${seq}`} className={at >= 0 ? 'on cont' : 'off cont'}>
                            <td {...fz(0)} className={`tkf-tick ${fz(0).className || ''}`}>
                              <input
                                type="checkbox"
                                checked={at >= 0}
                                disabled={disabled}
                                onChange={toggleMaterial(s.id, m, seq)}
                              />
                            </td>
                            <td {...fz(1)} className={`tkf-skuname ${fz(1).className || ''}`}>
                              <span className="tkf-mat">
                                {m.kind ? `${m.kind} · ` : ''}{m.name || t('xTicket.materialN', { n: seq })}
                              </span>
                              {!!m.colour && <small>{m.colour}</small>}
                            </td>
                            {cols.map((c, ci) => {
                              const f = fz(ci + 2);
                              const need = at >= 0 && c.required && answerMissing(c, row[c.key]);
                              return (
                                <td
                                  key={c.key}
                                  style={f.style}
                                  className={`${f.className || ''}${need ? ' missing' : ''}`.trim()}
                                >
                                  {at >= 0
                                    ? cell(c, row[c.key], setCell(s.id, at, c.key))
                                    : <span className="tkf-off">—</span>}
                                </td>
                              );
                            })}
                          </tr>
                        );
                      }),
                    ];
                  }
                  const lines = on ? linesOf(bag, s.id) : [{}];
                  return lines.map((row, i) => (
                    <tr key={`${s.id}#${i}`} className={`${on ? 'on' : 'off'}${i ? ' cont' : ''}`}>
                      <td {...fz(0)} className={`tkf-tick ${fz(0).className || ''}`}>
                        {!i && (
                          <input
                            type="checkbox"
                            checked={on}
                            disabled={disabled || !!takenBy(s.id)}
                            title={takenBy(s.id) ? t('xTicket.alreadyOn', { ticket: takenBy(s.id) }) : ''}
                            onChange={toggle(s.id)}
                          />
                        )}
                      </td>
                      {/* The line buttons live here, beside the SKU, not in a last column: the
                          table is ten columns wide and scrolls, and "add another colourway" was
                          three screens to the right of the thing it adds to. */}
                      <td {...fz(1)} className={`tkf-skuname ${fz(1).className || ''}`}>
                        {i ? <span className="tkf-cont">{t('xTicket.lineN', { n: i + 1 })}</span> : (
                          <>
                            <strong>{s.id}</strong>
                            <small>{s.subItem || s.projectItem || ''}</small>
                            {/* A dead box with no reason beside it reads as a broken box. The
                                ticket that holds the item is named, because that is where somebody
                                goes to change it. */}
                            {!!takenBy(s.id) && <small className="tkf-taken">{t('xTicket.takenOn', { ticket: takenBy(s.id) })}</small>}
                          </>
                        )}
                        {on && !disabled && (
                          <span className="tkf-lineact">
                            {i > 0 && (
                              <button type="button" className="tkf-lbtn drop" title={t('xTicket.removeLine')}
                                onClick={dropLine(s.id, i)}>&times;</button>
                            )}
                            {i === lines.length - 1 && (
                              <button type="button" className="tkf-lbtn"
                                title={t('xTicket.addLine')}
                                onClick={addLine(s.id)}>+</button>
                            )}
                          </span>
                        )}
                      </td>
                      {cols.map((c, ci) => {
                        const f = fz(ci + 2);
                        const need = on && c.required && answerMissing(c, row[c.key]);
                        return (
                          <td
                            key={c.key}
                            style={f.style}
                            className={`${f.className || ''}${need ? ' missing' : ''}`.trim()}
                          >
                            {on ? cell(c, row[c.key], setCell(s.id, i, c.key)) : <span className="tkf-off">—</span>}
                          </td>
                        );
                      })}
                    </tr>
                  ));
                })}
                {!skus.length && (
                  <tr><td className="sp-empty" colSpan={cols.length + 2}>{t('xTicket.noSkuInGroup')}</td></tr>
                )}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
}
